// src/pages/StudentStats.js
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import '../components/StudentStats.css';
import { useSchool } from "../hooks/useSchool";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const chartColors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

const StudentStats = () => {
  const [students, setStudents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [ticketThreshold, setTicketThreshold] = useState("");
  const schoolId = useSchool();

  useEffect(() => {
    const fetchData = async () => {
      if (!schoolId) return;
      const userSnap = await getDocs(
        query(collection(db, "users"), where("role", "==", "student"), where("schoolId", "==", schoolId))
      );
      setStudents(userSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      const ticketSnap = await getDocs(
        query(collection(db, "schools", schoolId, "tickets"))
      );
      setTickets(ticketSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    fetchData();
  }, [schoolId]);

  const getStudentStats = (studentId) => {
    const earned = Array(12).fill(0);
    const valueTypes = {};
    let totalEarned = 0;
    let totalSpent = 0;
    const history = [];

    for (const ticket of tickets) {
      if (ticket.studentId !== studentId) continue;
      const qty = ticket.quantity ?? 1;
      const date = ticket.timestamp?.toDate?.();
      const month = date?.getMonth();
      const type = ticket.valueType ?? "Other";

      if (qty > 0) {
        if (typeof month === "number") earned[month] += qty;
        valueTypes[type] = (valueTypes[type] || 0) + qty;
        totalEarned += qty;
      } else {
        totalSpent += Math.abs(qty);
      }

      history.push({
        date: date?.toLocaleDateString(),
        type,
        qty,
        source: ticket.givenBy?.name || "Unknown",
      });
    }

    return { earned, valueTypes, totalEarned, totalSpent, history };
  };

  const grouped = students.reduce((acc, student) => {
    const group = student.class ?? student.advisory ?? "Unassigned";
    if (!acc[group]) acc[group] = [];
    acc[group].push(student);
    return acc;
  }, {});

  const renderStats = (studentId) => {
    const { earned, valueTypes, totalEarned, totalSpent, history } = getStudentStats(studentId);

    return (
      <div className="stats-popup" onClick={(e) => e.stopPropagation()}>
        <div className="stats-header">
          <h4>📊 Ticket History</h4>
          <div style={{ textAlign: "right" }}>
            <div>🎟️ Earned: {totalEarned}</div>
            <div>🛍 Spent: {totalSpent}</div>
            <div>📦 Balance: {totalEarned - totalSpent}</div>
          </div>
        </div>
        <div className="chart-container">
          <Bar
            data={{
              labels: months,
              datasets: [
                {
                  label: "Monthly Earnings",
                  data: earned,
                  backgroundColor: "#3b82f6",
                },
              ],
            }}
            options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }}
          />
        </div>
        <div className="chart-container">
          <Bar
            data={{
              labels: Object.keys(valueTypes),
              datasets: [
                {
                  label: "By PBIS Value",
                  data: Object.values(valueTypes),
                  backgroundColor: chartColors,
                },
              ],
            }}
            options={{ responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }}
          />
        </div>
        <details className="ticket-history">
          <summary>🧾 Show Ticket Log</summary>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>From</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              {history.map((t, i) => (
                <tr key={i}>
                  <td>{t.date}</td>
                  <td>{t.type}</td>
                  <td>{t.source}</td>
                  <td>{t.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </div>
    );
  };

  const handlePrintFiltered = () => {
    const win = window.open("", "PrintFiltered");
    const filtered = students.filter((s) => {
      const { totalEarned } = getStudentStats(s.id);
      return !ticketThreshold || totalEarned <= parseInt(ticketThreshold);
    });

    const content = `
      <html>
      <head><title>Students ≤ ${ticketThreshold} Tickets</title></head>
      <body>
        <h2>🎯 Students with ≤ ${ticketThreshold} Tickets</h2>
        <table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;">
          <thead><tr><th>Name</th><th>Class</th><th>Total Tickets</th></tr></thead>
          <tbody>
            ${filtered
              .map((s) => {
                const total = getStudentStats(s.id).totalEarned;
                const klass = s.class ?? s.advisory ?? "Unassigned";
                return `<tr><td>${s.name}</td><td>${klass}</td><td>${total}</td></tr>`;
              })
              .join("")}
          </tbody>
        </table>
      </body>
      </html>
    `;
    win.document.write(content);
    win.document.close();
    win.print();
  };

  return (
    <div className="dashboard-container" onClick={() => setSelectedStudentId(null)}>
      <h2 className="section-title">📈 Student Stats</h2>
      <div className="filters-row">
        <input
          type="text"
          placeholder="🔍 Search by name..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
        />
        <input
          type="number"
          placeholder="🎯 Max tickets"
          className="search-input"
          value={ticketThreshold}
          onChange={(e) => setTicketThreshold(e.target.value)}
        />
        <button onClick={handlePrintFiltered}>🖨️ Print Filtered</button>
      </div>
      <div className="advisory-grid">
        {Object.entries(grouped).map(([advisory, members]) => (
          <div key={advisory} className="advisory-card">
            <h3>{advisory}</h3>
            <ul className="student-list">
              {members
                .filter((s) =>
                  s.name?.toLowerCase().includes(searchTerm)
                )
                .filter((s) => {
                  const { totalEarned } = getStudentStats(s.id);
                  return !ticketThreshold || totalEarned <= parseInt(ticketThreshold);
                })
                .map((student) => (
                  <li
                    key={student.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedStudentId((prev) =>
                        prev === student.id ? null : student.id
                      );
                    }}
                    className={`student-name ${
                      selectedStudentId === student.id ? "active" : ""
                    }`}
                  >
                    {student.name}
                    {selectedStudentId === student.id && renderStats(student.id)}
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StudentStats;